import { computed, ref } from 'vue';
import { ApiService } from '@/api';
import { clone } from '@/utils/common';

type LocationList = Awaited<ReturnType<typeof ApiService.getLocations>>;
type LocationItem = LocationList[number];
export type Location = LocationItem & { children?: Location[] };

const data = ref<LocationList>([]);

export function useLocation() {
    const fetch = async () => {
        const locations = await ApiService.getLocations({});
        data.value = locations;
    };

    const tree = (items: Location[] = []): Location[] => {
        const root = [];
        const lookup: any = {};
        for (const item of items) {
            const id = item.id;
            const parentId = item.parentId;

            if (!lookup[id]) lookup[id] = { ['children']: [] };

            lookup[id] = { ...item, ['children']: lookup[id]['children'] };

            const TreeItem = lookup[id];

            if (!parentId) {
                root.push(TreeItem);
            } else {
                if (!lookup[parentId]) lookup[parentId] = { ['children']: [] };

                lookup[parentId]['children'].push(TreeItem);
            }
        }
        return root;
    };

    const locations = computed(() => {
        return tree(clone(data.value || []));
    });

    const states = computed(() => {
        const items = (data.value || []).filter((x) => x.type == 'state');
        return tree(clone(items));
    });

    const districts = computed(() => {
        const items = (data.value || []).filter((x) => ['state', 'district'].includes(x.type));
        return tree(clone(items));
    });

    const areas = computed(() => {
        const items = (data.value || []).filter((x) => ['state', 'district', 'area'].includes(x.type));
        return tree(clone(items));
    });

    const pincodes = computed(() => {
        const pincodes = (data.value || []).filter((x) => x.type == 'pincode');
        const areas = (data.value || []).filter((x) => x.type == 'area');
        const districts = (data.value || []).filter((x) => x.type == 'district');
        const states = (data.value || []).filter((x) => x.type == 'state');
        return pincodes.map((p) => {
            let area = areas.find((a) => a.id == p.parentId);
            let district = area ? districts.find((d) => d.id == area?.parentId) : null;
            let state = district ? states.find((s) => s.id == district?.parentId) : null;
            return {
                pincode: p.title,
                area: area?.title,
                district: district?.title,
                state: state?.title,
            };
        });
    });

    const duplicates = computed(() => {
        const items = (data.value || []).filter((x) => ['pincode'].includes(x.type));
        const pincodes: Location[] = clone(items);
        const titles: string[] = pincodes.map((x) => x.title);
        const set = new Set(titles);
        const d = titles.filter((t) => {
            if (set.has(t)) {
                set.delete(t);
            } else {
                return t;
            }
        });
        return d.sort();
    });

    const getLocation = (id: string | number) => {
        return data.value?.find((x) => x.id == id);
    };

    return {
        locations,
        getLocation,
        states,
        districts,
        areas,
        pincodes,
        fetch,
        duplicates,
    };
}
